import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "../../../auth/useAuth";
import ApiAuth from "../../../app/api/public/system/ApiAuth";
import InputWrapper from "../../components/input/__wrapper";
import InputText from "../../components/inputs/text";
import InputPassword from "../../components/inputs/password";
import Button from "../../components/button";
import "./styles.css";

// ?redirect= — мост для control/{id}/live: страницу стрима шлют сторонней ссылкой (бот) БЕЗ
// ?token= в принципе — так и задумано, каждый заход с бота требует явного входа (см. бэк
// ControlAuthMiddleware). Она редиректит сюда с ?redirect=<та же ссылка>, а после логина (или
// если уже залогинены — см. isAuthReady ниже) уводим обратно, ПОДСТАВИВ свежий ?token= в саму
// ссылку — никакой общей куки между поддоменами не заводим (сознательно не расширяем зону
// доступности токена, см. обсуждение). Валидируем redirect строго — тот же поддомен-суффикс,
// что и у SPA — иначе открытый редирект: чужой ?redirect=https://evil.example увёл бы
// залогинившегося оператора куда угодно (а с этой версией — ещё и с его токеном в URL).
const REDIRECT_HOST_SUFFIX = '.' + window.location.hostname.split('.').slice(1).join('.');

function safeRedirectTarget(raw) {
    if (!raw) return null;
    let url;
    try {
        url = new URL(raw);
    } catch {
        return null;
    }
    if (url.protocol !== 'https:') return null;
    if (REDIRECT_HOST_SUFFIX === '.' || !url.hostname.endsWith(REDIRECT_HOST_SUFFIX)) return null;
    return url;
}

function urlWithToken(url, token) {
    const withToken = new URL(url.toString());
    withToken.searchParams.set('token', token);
    return withToken.toString();
}

// DEV-предзаполнение формы: значения из .env (REACT_APP_AUTH_*), но ТОЛЬКО в деве.
// В прод-сборке NODE_ENV='production' → пустые строки (минификатор вырезает ветку,
// пароль в бандл не попадает). Задаётся в .env; пусто/нет переменной — поля пустые.
const DEV_DEFAULT_USERNAME = process.env.NODE_ENV === 'development' ? (process.env.REACT_APP_AUTH_LOGIN || '') : '';
const DEV_DEFAULT_PASSWORD = process.env.NODE_ENV === 'development' ? (process.env.REACT_APP_AUTH_PASSWORD || '') : '';

// Страница входа: username/password → useAuth().login → /nodes (или ?redirect=, см. выше).
// Ошибки валидации — под полями, общая ошибка — над кнопкой.
export default function LoginPage() {
    const { isAuthReady, isAuthenticated, accessToken, login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectTarget = safeRedirectTarget(searchParams.get('redirect'));

    const [_username, set_username] = useState(DEV_DEFAULT_USERNAME);
    const [_password, set_password] = useState(DEV_DEFAULT_PASSWORD);
    const [errors, setErrors] = useState(null);
    const [message, setMessage] = useState(null);
    const [isSending, setIsSending] = useState(false);

    // Уже залогинены (сессия восстановлена из localStorage) — уводим сразу же, без формы.
    if (isAuthReady && isAuthenticated) {
        if (redirectTarget && accessToken) {
            window.location.href = urlWithToken(redirectTarget, accessToken);
            return null;
        }
        return <Navigate to="/nodes" replace={true}/>;
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSending) return;
        setIsSending(true);
        setErrors(null);
        setMessage(null);
        const answer = await login({ username: _username, password: _password });
        setIsSending(false);
        if (answer.result) {
            // Свежий токен — из стора напрямую (login() его не возвращает, только {result,...}),
            // ApiAuth.SetInitData уже отработал синхронно внутри login() к этому моменту.
            const freshToken = redirectTarget ? new ApiAuth().getAccessToken() : null;
            if (redirectTarget && freshToken) {
                window.location.href = urlWithToken(redirectTarget, freshToken);
                return;
            }
            navigate('/nodes');
            return;
        }
        setErrors(answer.errors);
        setMessage(answer.message ?? 'Не удалось войти');
    };

    return <div className="login">
        <form className="login__card" onSubmit={handleSubmit}>
            <div className="login__brand">Cloud Phone</div>
            <div className="login__subtitle">Вход в панель управления</div>
            <InputWrapper>
                <InputText
                    className="input_wf"
                    value={_username}
                    errors={errors ? errors.username : null}
                    autoComplete="username"
                    onChange={(event) => set_username(event.target.value)}
                >
                    Логин
                </InputText>
            </InputWrapper>
            <InputWrapper>
                <InputPassword
                    className="input_wf"
                    value={_password}
                    errors={errors ? errors.password : null}
                    onChange={(event) => set_password(event.target.value)}
                >
                    Пароль
                </InputPassword>
            </InputWrapper>
            {message ? <div className="login__message">{message}</div> : null}
            <Button className="btn-success btn_wf" type="submit" disabled={isSending}>
                {isSending ? 'Вход…' : 'Войти'}
            </Button>
        </form>
    </div>;
}
